
import { getUninvoicedJobs, getUninvoicedJobsCsv, lookupCustomer, UninvoicedJobsParams } from '@/api';
import LookupCustomerCode from '@/components/jobBooking/AdvancedJobBooking/LookupCustomerCode.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import { UninvoicedJob, UninvoicedJobsSummary } from '@/models/reports';
import { Header, User } from '@/models/main';
import { notificationErrorText } from '@/utils/PortalConstants';
import { formatDateStringWithTZ } from '@/utils/InputProcessingUtilities';
import { blobDownloader } from '@/utils/Common';
import * as Sentry from '@sentry/vue';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';

@Component({
  components: {
    BackButton,
    LookupCustomerCode,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class UninvoicedJobsReport extends Vue {
  private user!: User;
  private isLoading = false;
  private isCsvLoading = false;
  private formatDateStringWithTZ = formatDateStringWithTZ;

  // Filters
  private statusFilter: 'uninvoiced' | 'dummy' = 'uninvoiced';
  private statusOptions = [
    { text: 'Uninvoiced', value: 'uninvoiced' },
    { text: 'Dummy', value: 'dummy' },
  ];
  private allCustomers = true;
  private customerCode = '';
  private autocompleteCustomer = lookupCustomer;
  private paymentStatusFilter = '';
  private fromDate = '';
  private toDate = '';
  private menuFromDate = false;
  private menuToDate = false;

  private paymentStatusOptions = [
    { text: 'All', value: '' },
    { text: 'On Hold', value: 'on_hold' },
    { text: 'Paid', value: 'paid' },
    { text: 'Pending', value: 'pending' },
  ];

  // Data
  private jobs: UninvoicedJob[] = [];
  private summary: UninvoicedJobsSummary | null = null;
  private totalCount = 0;

  // Pagination
  private pagination = {
    page: 1,
    perPage: 50,
    totalPages: 0,
    perPageOptions: [20, 50, 100],
  };

  // Table
  private headers: Header[] = [
    { text: 'Job ID', value: 'rockhopper_id', align: 'left' },
    { text: 'Date', value: 'rockhopper_date', align: 'left' },
    { text: 'Customer', value: 'customer_code', align: 'left' },
    { text: 'Invoice No', value: 'invoice_no', align: 'left' },
    { text: 'Completed At', value: 'completed_at', align: 'left' },
    { text: 'Payment Status', value: 'payment_status', align: 'center' },
    { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
  ];

  private buildParams(): UninvoicedJobsParams {
    const params: UninvoicedJobsParams = {
      status: this.statusFilter,
      page: this.pagination.page,
      page_size: this.pagination.perPage,
    };
    if (!this.allCustomers && this.customerCode) {
      params.customer = this.customerCode;
    }
    if (this.paymentStatusFilter) {
      params.payment_status = this.paymentStatusFilter;
    }
    if (this.fromDate) {
      params.from_date = this.fromDate;
    }
    if (this.toDate) {
      params.to_date = this.toDate;
    }
    return params;
  }

  private async fetchData(): Promise<void> {
    this.isLoading = true;
    const params = this.buildParams();
    await getUninvoicedJobs(params)
      .then(res => {
        this.jobs = res.results;
        this.totalCount = res.count;
        this.summary = res.summary;
        this.pagination.totalPages = Math.ceil(res.count / this.pagination.perPage);
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Uninvoiced Jobs report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private filterByPaymentStatus(status: string): void {
    this.paymentStatusFilter = status;
    this.resetAndFetch();
  }

  private resetAndFetch(): void {
    this.pagination.page = 1;
    this.fetchData();
  }

  private onPageChange(): void {
    this.fetchData();
  }

  private onPerPageChange(): void {
    this.pagination.page = 1;
    this.fetchData();
  }

  private async downloadCsv(): Promise<void> {
    this.isCsvLoading = true;
    const params = this.buildParams();
    await getUninvoicedJobsCsv(params)
      .then(res => {
        blobDownloader(res);
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error downloading CSV',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isCsvLoading = false;
      });
  }

  // Customer code
  @Watch('allCustomers')
  private onAllCustomersChange(val: boolean): void {
    if (val) {
      this.customerCode = '';
    }
  }

  private setCustomerCode(code: string): void {
    this.customerCode = code;
  }

  private clearCustomerCode(): void {
    this.customerCode = '';
  }

  // Helpers
  private paymentStatusColor(status: string): string {
    switch (status) {
      case 'on_hold':
        return 'orange';
      case 'paid':
        return 'green';
      case 'pending':
        return 'amber darken-2';
      default:
        return 'grey';
    }
  }

  private badgeCount(count: number): string | number {
    if (this.paymentStatusFilter !== '' && count === 0) {
      return '–';
    }
    return count;
  }

  private formatPaymentStatus(status: string): string {
    switch (status) {
      case 'on_hold':
        return 'On Hold';
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  }

  private mounted(): void {
    const now = new Date();
    const year = now.getFullYear();
    this.fromDate = `${year}-01-01`;
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tMonth = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const tDay = String(tomorrow.getDate()).padStart(2, '0');
    this.toDate = `${tomorrow.getFullYear()}-${tMonth}-${tDay}`;
  }
}
